import { getQueryStringFromOptions, getResponse } from '@satellite/services/serviceUtilities';
import companyService from '@satellite/services/company-service';
import { cacheableGet } from '@satellite/services/httpClient';

/**
 * options: { fields: string[], joins: string[], includeMetaData: boolean }
 */

export default {
  /**
   * @deprecated
   * Use {@link getUserByIdV2} instead
   * @param {string} userId - User ID
   * @param {Object} [params] - Request parameters
   * @param {Object} [options] - Request options
   * @returns {Promise<Object>} The response data
   */
  async getUserById(userId, params, options = {}) {
    return this.getUserByIdV2(userId, {
      query: options || {},
      getResponseConfig: options || {},
      requestConfig: params
        ? {
            params
          }
        : {}
    });
  },

  /**
   * @template TRequest - The type of the request data
   * @template TResponse - The type of the response data
   *
   * Get user by ID with improved API contract
   * @param {string} userId - User ID
   * @param {Object} [options] - Request options
   * @param {import('@satellite/services/serviceUtilities').GetResponseConfig} [options.getResponseConfig] - Response handling options (includeMetaData)
   * @param {import('@satellite/services/serviceUtilities').GetQueryStringOptions} [options.query] - Query string options (fields, joins)
   * @param {import('axios').AxiosRequestConfig<TRequest>} [options.requestConfig] - Axios request config (params, signal, headers, etc.)
   * @param {boolean} [options.enableCache] - Enable inflight request caching
   * @returns {Promise<TResponse>} The response data
   */
  async getUserByIdV2(userId, options) {
    const { getResponseConfig, requestConfig, query, enableCache } = {
      getResponseConfig: {},
      requestConfig: {},
      query: {},
      enableCache: false,
      ...options
    };
    const queryString = getQueryStringFromOptions(query);
    const url = `/user/${userId}${queryString ? `?${queryString}` : ''}`;

    const response = await cacheableGet(url, {
      ...requestConfig,
      enableCache
    });
    return getResponse(response, getResponseConfig);
  },

  async getUsers(params, options = {}) {
    const queryString = getQueryStringFromOptions(options);
    const response = await axios.get(`user?${queryString}`, {
      params
    });
    return getResponse(response, options);
  },

  /**
   * @template TRequest - The type of the request data
   * @template TResponse - The type of the response data
   *
   * Get users with improved API contract
   * @param {Object} [options] - Request options
   * @param {import('@satellite/services/serviceUtilities').GetResponseConfig} [options.getResponseConfig] - Response handling options (includeMetaData)
   * @param {import('@satellite/services/serviceUtilities').GetQueryStringOptions} [options.query] - Query string options (fields, joins)
   * @param {import('axios').AxiosRequestConfig<TRequest>} [options.requestConfig] - Axios request config (params, signal, headers, etc.)
   * @param {boolean} [options.enableCache] - Enable inflight request caching
   * @returns {Promise<TResponse>} The response data
   */
  async getUsersV2(options) {
    const { getResponseConfig, requestConfig, query, enableCache } = {
      getResponseConfig: {},
      requestConfig: {},
      query: {},
      enableCache: false,
      ...options
    };
    const queryString = getQueryStringFromOptions(query);
    const url = `/user${queryString ? `?${queryString}` : ''}`;

    const response = await cacheableGet(url, {
      ...requestConfig,
      enableCache
    });
    return getResponse(response, getResponseConfig);
  },

  // TODO: Add request config to all services
  async updateUser(userId, data, options = {}, requestConfig = {}) {
    const response = await axios.patch(`user/${userId}`, data, requestConfig);
    return getResponse(response, options);
  },

  async setCompanyOnUser(user) {
    const company = await companyService.getCompanyById(user.companyId, {}, { fields: ['name'] });
    user.company = company;
    return user;
  }
};
